import React, { useState } from 'react';
import { Box, Typography, IconButton, Menu, MenuItem, Tooltip } from '@mui/material';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import UnarchiveOutlinedIcon from '@mui/icons-material/UnarchiveOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useTranslation } from 'react-i18next';
import { jobTypeName } from '../../../utils/jobType';

// job.job_status → chip renk + label. tr.json/jobStatuses altındaki
// tüm değerler burada karşılığını bulur; olmayan status için gri gösterir.
const JOB_STATUS_COLORS = {
  YAZIMDA:                    { bg: '#e0e7ff', fg: '#4338ca' },
  DEGERLENDIRMEDE:            { bg: '#fef3c7', fg: '#b45309' },
  ONAYLANDI:                  { bg: '#d1fae5', fg: '#065f46' },
  DEVRALINDI:                 { bg: '#dbeafe', fg: '#1d4ed8' },
  REDDEDILDI:                 { bg: '#fee2e2', fg: '#b91c1c' },
  KAPATILDI:                  { bg: '#f3f4f6', fg: '#374151' },
  DANISMANLIK_SONLANDIRILDI:  { bg: '#f3f4f6', fg: '#374151' },
  ORTAK_BULUNDU:              { bg: '#e0f2fe', fg: '#0369a1' },
  ASAMA_BIR_YAZIMDA:          { bg: '#e0e7ff', fg: '#4338ca' },
  ASAMA_BIR_DEGERLENDIRMEDE:  { bg: '#fef3c7', fg: '#b45309' },
  ASAMA_BIR_ONAYLANDI:        { bg: '#d1fae5', fg: '#065f46' },
  ASAMA_IKI_YAZIMDA:          { bg: '#e0e7ff', fg: '#4338ca' },
  ASAMA_IKI_DEGERLENDIRMEDE:  { bg: '#fef3c7', fg: '#b45309' },
  ASAMA_IKI_ONAYLANDI:        { bg: '#d1fae5', fg: '#065f46' },
  TAMAMLANDI:                 { bg: '#e6fffa', fg: '#0f766e' },
};

const JOB_TYPE_COLORS = {
  AR_GE: '#7c3aed', TASARIM_MERKEZI: '#4338ca', TEKNOPARK: '#0d9488',
  TUBITAK: '#d97706', ULUSLARARASI: '#0284c7',
};

const STATUS_DOT = {
  PENDING: '#f59e0b', IN_PROGRESS: '#3b82f6', COMPLETED: '#10b981', OVERDUE: '#ef4444',
};

const STATUS_LABEL = {
  PENDING: 'Bekliyor', IN_PROGRESS: 'Devam Ediyor', COMPLETED: 'Tamamlandı', OVERDUE: 'Gecikmiş',
};

function fmtDate(s) {
  if (!s) return null;
  const d = new Date(s);
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}.${mm}.${d.getFullYear()}`;
}

function daysLeft(s) {
  if (!s) return null;
  const e = new Date(s); const n = new Date();
  e.setHours(0,0,0,0); n.setHours(0,0,0,0);
  return Math.ceil((e - n) / 86400000);
}

function daysColor(d) {
  if (d === null) return '#94a3b8';
  if (d <= 0) return '#ef4444';
  if (d <= 7) return '#f59e0b';
  return '#94a3b8';
}

function getReport(item) {
  const r = item?.job?.job_reports;
  if (!Array.isArray(r) || !item?.report_id) return null;
  return r.find((x) => String(x.id) === String(item.report_id)) || null;
}

const KanbanCard = ({ item, columnColor, onComplete, onArchive, onUnarchive, isRoot, onCardClick, isOverlay = false, flowTags = [], reportTypeMap = null }) => {
  const { t } = useTranslation();
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [hovered, setHovered] = useState(false);

  // disabled: true on the DragOverlay clone — without this, both copies
  // register the same id and dnd-kit attaches the transform to the wrong node.
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: String(item.id),
    disabled: isOverlay,
  });
  const style = isOverlay
    ? {}
    : { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.3 : 1 };

  const job = item?.job || {};
  const report = getReport(item);
  const firmName = job?.contract?.firm?.firm_name || job?.firm_short_name || '-';
  const jobTypeAdi = jobTypeName(job);
  const jobTypeColor = JOB_TYPE_COLORS[jobTypeAdi] || '#94a3b8';
  // Rapor turu ismi: once explicit `reportTypeName` (yeni kayitlar),
  // sonra parent'tan gelen id->name map lookup (job_reports JSONB'de ID
  // olarak saklanan eski kayitlar), en son ham deger (fallback).
  // reportTypeMap yoksa/eslesme yoksa yine ID gorunmesin diye 'null' donar.
  const reportTypeRaw = report?.reportType;
  const reportTypeName = report?.reportTypeName
    || (reportTypeMap && reportTypeRaw != null ? reportTypeMap[String(reportTypeRaw)] : null);
  const reportType = reportTypeName || (typeof reportTypeRaw === 'string' ? reportTypeRaw : null);
  const tags = Array.isArray(report?.reportTags) ? report.reportTags : [];
  const status = report?.status || null;
  const statusDot = STATUS_DOT[status] || null;
  const days = daysLeft(report?.endDate);
  const isCompleted = !!item.completion_date;
  const isArchived = item.is_archived;

  // İş durumu chip'i için label + renk (tr.json/jobs.jobStatuses.*).
  const jobStatus = job?.job_status || null;
  const jobStatusStyle = jobStatus ? JOB_STATUS_COLORS[jobStatus] : null;
  const jobStatusLabel = jobStatus
    ? t(`jobs.jobStatuses.${jobStatus}`, { defaultValue: jobStatus })
    : null;

  return (
    <Box
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onDoubleClick={(e) => {
        // Cift tik → info modal. Sensor activationConstraint (delay:180)
        // sayesinde tek/cift tik drag'i tetiklemez; sadece basili tut+hareket
        // ettir sonrasinda drag baslar.
        if (isOverlay || isArchived) return;
        e.stopPropagation();
        onCardClick?.(item);
      }}
      sx={{
        position: 'relative',
        bgcolor: '#fff',
        borderRadius: '8px',
        border: '1px solid',
        borderColor: hovered ? '#d4d4d8' : '#e4e4e7',
        boxShadow: isDragging ? '0 8px 24px rgba(0,0,0,0.15)' : 'none',
        mb: 1,
        // Cift-tik + basili-tut pattern'i: normal pointer, sadece drag
        // aninda grabbing. Kullanici cift tikla info actigini, basili tutup
        // surukleyerek sutun degistirdigini goruyor.
        cursor: isDragging ? 'grabbing' : 'pointer',
        touchAction: 'none',
        transition: 'border-color 0.15s',
        overflow: 'hidden',
        userSelect: 'none',
      }}
    >
      {/* Archived overlay + Arşivden çıkar butonu */}
      {isArchived && (
        <Box sx={{
          position: 'absolute', inset: 0, bgcolor: 'rgba(255,255,255,0.72)',
          zIndex: 2, display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexDirection: 'column', gap: 1,
        }}>
          <Typography sx={{
            fontSize: '0.7rem', color: '#a1a1aa', fontWeight: 600,
            letterSpacing: '0.08em', textTransform: 'uppercase',
          }}>
            Arşivlenmiş
          </Typography>
          {onUnarchive && (
            <Box
              component="button"
              onMouseDown={(e) => e.stopPropagation()}
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => { e.stopPropagation(); onUnarchive(item); }}
              sx={{
                display: 'inline-flex', alignItems: 'center', gap: 0.6,
                px: 1.2, py: 0.5, borderRadius: '6px',
                bgcolor: '#fff', border: '1px solid #d4d4d8',
                fontSize: '0.72rem', fontWeight: 600, color: '#3b82f6',
                cursor: 'pointer',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                '&:hover': { bgcolor: '#eff6ff', borderColor: '#3b82f6' },
              }}
            >
              <UnarchiveOutlinedIcon sx={{ fontSize: '0.95rem' }} />
              Arşivden Çıkar
            </Box>
          )}
        </Box>
      )}

      <Box sx={{ p: 1.5 }}>
        {/* Row 1: Firm + menu */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 0.5 }}>
          <Typography sx={{ flex: 1, fontWeight: 600, fontSize: '0.82rem', color: '#18181b', lineHeight: 1.4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {firmName}
          </Typography>
          {onCardClick && (
            <Tooltip title={t('reportManagement.cardDetail.viewDetails', { defaultValue: 'Detayları Görüntüle' })}>
              <IconButton
                size="small"
                onMouseDown={(e) => { e.stopPropagation(); }}
                onPointerDown={(e) => { e.stopPropagation(); }}
                onClick={(e) => { e.stopPropagation(); e.preventDefault(); onCardClick(item); }}
                sx={{ p: 0.25, ml: 0.5, mt: -0.25, opacity: hovered ? 0.7 : 0, transition: 'opacity 0.15s', '&:hover': { opacity: 1 } }}
              >
                <InfoOutlinedIcon sx={{ fontSize: '1rem', color: '#3b82f6' }} />
              </IconButton>
            </Tooltip>
          )}
          {isRoot && (
            <IconButton
              size="small"
              onMouseDown={(e) => { e.stopPropagation(); }}
              onPointerDown={(e) => { e.stopPropagation(); }}
              onClick={(e) => { e.stopPropagation(); e.preventDefault(); setMenuAnchor(e.currentTarget); }}
              sx={{ p: 0.25, ml: 0.25, mt: -0.25, opacity: hovered ? 0.6 : 0, transition: 'opacity 0.15s', '&:hover': { opacity: 1 } }}
            >
              <MoreHorizIcon sx={{ fontSize: '1rem', color: '#71717a' }} />
            </IconButton>
          )}
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={() => setMenuAnchor(null)}
            PaperProps={{ elevation: 8, sx: { minWidth: 160, borderRadius: '8px', mt: 0.5, '& .MuiMenuItem-root': { fontSize: '0.8rem', py: 0.75, gap: 1 } } }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <MenuItem onClick={() => { setMenuAnchor(null); onComplete?.(item); }}>
              <CheckCircleOutlineIcon sx={{ fontSize: '0.95rem', color: '#71717a' }} />
              Tamamla
            </MenuItem>
            <MenuItem onClick={() => { setMenuAnchor(null); onArchive?.(item); }}>
              <ArchiveOutlinedIcon sx={{ fontSize: '0.95rem', color: '#71717a' }} />
              Arşivle
            </MenuItem>
          </Menu>
        </Box>

        {/* Row 2: Job title */}
        {job.job_title && (
          <Typography sx={{ fontSize: '0.74rem', color: '#71717a', lineHeight: 1.35, mb: job.project_number ? 0.25 : 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {job.job_title}
          </Typography>
        )}

        {/* Proje No — is_type TUBITAK sarti kaldirildi. Turktek gibi
             farklı is tiplerindeki dokumanlarda da proje no varsa gosterilsin
             (kullanici bildirimi: info'da gozukuyor ama card'da yoktu). */}
        {job.project_number && (
          <Typography
            variant="caption"
            sx={{ display: 'block', fontSize: '0.68rem', color: '#a1a1aa', fontWeight: 500, mb: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            {t('reportManagement.card.projectNumberLabel', { defaultValue: 'Proje No' })}: {job.project_number}
          </Typography>
        )}

        {/* İş Durumu chip'i — akış içindeki işin şu anki job_status'u.
            Kullanıcı istegi: "iş cardlarında statüleri felan göstermeliyiz". */}
        {jobStatusLabel && (
          <Box sx={{ display: 'flex', mb: 1 }}>
            <Box sx={{
              display: 'inline-flex', alignItems: 'center',
              px: 0.9, py: 0.25, borderRadius: '4px',
              bgcolor: jobStatusStyle?.bg || '#f3f4f6',
              color: jobStatusStyle?.fg || '#374151',
              fontSize: '0.65rem', fontWeight: 700,
              letterSpacing: '0.02em', lineHeight: 1.3,
            }}>
              {jobStatusLabel}
            </Box>
          </Box>
        )}

        {/* Row 3: Metadata line */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5, mb: tags.length > 0 ? 1 : 0 }}>
          {/* Job type dot + label */}
          {jobTypeAdi && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
              <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: jobTypeColor, flexShrink: 0 }} />
              <Typography sx={{ fontSize: '0.68rem', color: '#52525b', fontWeight: 500 }}>
                {t(`jobs.jobTypes.${jobTypeAdi}`, { defaultValue: jobTypeAdi })}
              </Typography>
            </Box>
          )}

          {/* Separator */}
          {jobTypeAdi && reportType && (
            <Typography sx={{ fontSize: '0.6rem', color: '#d4d4d8' }}>·</Typography>
          )}

          {/* Report type */}
          {reportType && (
            <Typography sx={{ fontSize: '0.68rem', color: '#71717a', fontWeight: 500 }}>
              {reportType}
            </Typography>
          )}

          {/* Separator */}
          {(jobTypeAdi || reportType) && statusDot && (
            <Typography sx={{ fontSize: '0.6rem', color: '#d4d4d8' }}>·</Typography>
          )}

          {/* Status */}
          {statusDot && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.35 }}>
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: statusDot, flexShrink: 0 }} />
              <Typography sx={{ fontSize: '0.66rem', color: '#71717a', fontWeight: 500 }}>
                {t(`reportManagement.statuses.${status}`, { defaultValue: STATUS_LABEL[status] })}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Row 4a: Akis-ozel etiketler (renkli). Rapor tag'lerinden once
             gosterilir; akisa ozel context daha onemli. */}
        {flowTags.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.4, mb: tags.length > 0 ? 0.5 : 1 }}>
            {flowTags.slice(0, 3).map((tag) => (
              <Typography
                key={tag.id}
                sx={{
                  fontSize: '0.62rem',
                  color: '#fff',
                  bgcolor: tag.color,
                  borderRadius: '4px',
                  px: 0.6,
                  py: 0.15,
                  fontWeight: 600,
                  lineHeight: 1.4,
                }}
              >
                {tag.name}
              </Typography>
            ))}
            {flowTags.length > 3 && (
              <Typography sx={{ fontSize: '0.62rem', color: '#a1a1aa', fontWeight: 500, lineHeight: 1.4, py: 0.15 }}>
                +{flowTags.length - 3}
              </Typography>
            )}
          </Box>
        )}

        {/* Row 4b: Rapor tags (mevcut — legacy) */}
        {tags.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.4, mb: 1 }}>
            {tags.slice(0, 3).map((tag) => (
              <Typography
                key={tag}
                sx={{
                  fontSize: '0.62rem',
                  color: '#71717a',
                  bgcolor: '#f4f4f5',
                  borderRadius: '4px',
                  px: 0.6,
                  py: 0.15,
                  fontWeight: 500,
                  lineHeight: 1.4,
                }}
              >
                {tag}
              </Typography>
            ))}
            {tags.length > 3 && (
              <Typography sx={{ fontSize: '0.62rem', color: '#a1a1aa', fontWeight: 500, lineHeight: 1.4, py: 0.15 }}>
                +{tags.length - 3}
              </Typography>
            )}
          </Box>
        )}

        {/* Row 5: Date bar */}
        {(report?.startDate || report?.endDate) && !isCompleted && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              bgcolor: '#fafafa',
              border: '1px solid #f4f4f5',
              borderRadius: '6px',
              px: 0.85,
              py: 0.55,
            }}
          >
            <Typography sx={{ fontSize: '0.7rem', color: '#52525b', fontWeight: 500 }}>
              {fmtDate(report.startDate) || '?'} → {fmtDate(report.endDate) || '?'}
            </Typography>
            {days !== null && (
              <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: daysColor(days) }}>
                {days < 0 ? `${Math.abs(days)}g gecikme` : days === 0 ? 'Bugün' : `${days}g`}
              </Typography>
            )}
          </Box>
        )}

        {/* Completed */}
        {isCompleted && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              bgcolor: '#f0fdf4',
              border: '1px solid #dcfce7',
              borderRadius: '6px',
              px: 0.85,
              py: 0.55,
            }}
          >
            <CheckCircleOutlineIcon sx={{ fontSize: '0.85rem', color: '#22c55e' }} />
            <Typography sx={{ fontSize: '0.7rem', fontWeight: 500, color: '#166534' }}>
              {new Date(item.completion_date).toLocaleDateString('tr-TR')}
            </Typography>
            {item.submission_date && (
              <Typography sx={{ fontSize: '0.66rem', color: '#15803d', ml: 'auto' }}>
                Gönderim {new Date(item.submission_date).toLocaleDateString('tr-TR')}
              </Typography>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default React.memo(KanbanCard);
